import React from 'react';
import { Combobox } from 'components/ui/combobox';
import { MultiSelectCombobox } from 'components/ui/multi-select-combobox';
import { Level } from 'components/Dropdowns';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn, fyDateRange } from 'lib/utils';

interface Option {
    value: string;
    label: string;
}

export interface LhcDropdownLevel {
    id: number;
    name: string;
    options: { ou_code: string; ou_name: string }[];
    selected?: string[];
}

const LOCKED_MULTI_VALUE = '__locked_multiple__';

interface DropdownsProps {
    year: string;
    years?: string[];
    levels: Level[];
    loading: boolean;
    onSelectChange: (levelId: number | string, value: string[]) => void;
    onYearChange?: (year: string) => void;

    lhcDropdownLevels: LhcDropdownLevel[];
    onLhcLevelChange: (levelId: number, value: string) => void;

    dateFrom: Date | undefined;
    onDateFromChange: (date: Date | undefined) => void;
    dateTo: Date | undefined;
    onDateToChange: (date: Date | undefined) => void;

    onBatchSelect?: (updates: Array<{ levelId: number | string; value: string[] }>) => void;
}

const SingleSelectFilter: React.FC<{
    label: string;
    options: string[];
    value: string;
    onValueChange: (val: string) => void;
    disabled?: boolean;
}> = ({ label, options, value, onValueChange, disabled }) => {
    const opts: Option[] = options.map(opt => ({ value: opt, label: opt }));
    return (
        <div className="min-w-0">
            <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">{label}</label>
                <Combobox
                    options={opts}
                    value={value}
                    onValueChange={onValueChange}
                    placeholder="-- select --"
                    disabled={disabled}
                    buttonClassName="h-10 w-full px-2.5 text-sm"
                />
            </div>
        </div>
    );
};

const DateRangeFilter: React.FC<{
    from: Date | undefined;
    to: Date | undefined;
    onFromChange: (date: Date | undefined) => void;
    onToChange: (date: Date | undefined) => void;
    year: string;
}> = ({ from, to, onFromChange, onToChange, year }) => {
    const [dateOpen, setDateOpen] = React.useState(false);
    const [dateTarget, setDateTarget] = React.useState<"from" | "to">("from");

    // Dates outside the selected financial year are greyed out: picking one
    // would silently return no rows, since every query is FY-bounded server-side.
    const fyRange = fyDateRange(year);

    const handleDateSelect = (selectedDate?: Date) => {
        if (!selectedDate) return;

        if (dateTarget === "from") {
            onFromChange(selectedDate);

            if (to && selectedDate > to) {
                onToChange(undefined);
            }
            setDateOpen(false);
            return;
        }
        onToChange(selectedDate);
        setDateOpen(false);
    };

    return (
        <div className="min-w-0">
            <label className="block text-sm font-medium mb-1 text-gray-700">Date Range</label>

            <div className="flex items-center border rounded-md bg-background h-10">
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <div className="flex divide-x w-full">
                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setDateTarget("from");
                                    setDateOpen(true);
                                }}
                                className={cn(
                                    "h-10 px-2 py-1 justify-start text-left font-normal rounded-none rounded-l-md hover:bg-muted/50 text-xs min-w-[145px]",
                                    !from && "text-gray-500"
                                )}
                            >
                                <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />

                                <div className="flex flex-col min-w-0 leading-tight">
                                    <span className="text-xs font-normal text-gray-600">From Date</span>
                                    <span className="text-xs truncate">
                                        {from ? format(from, "dd MMM yyyy") : "Start"}
                                    </span>
                                </div>
                            </Button>
                        </PopoverTrigger>

                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setDateTarget("to");
                                    setDateOpen(true);
                                }}
                                className={cn(
                                    "h-10 px-2 py-1 justify-start text-left font-normal rounded-none rounded-r-md hover:bg-muted/50 text-xs min-w-[145px]",
                                    !to && "text-gray-500"
                                )}
                            >
                                <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />

                                <div className="flex flex-col min-w-0 leading-tight">
                                    <span className="text-xs font-normal text-gray-600">To Date</span>
                                    <span className="text-xs truncate">
                                        {to ? format(to, "dd MMM yyyy") : "Latest"}
                                    </span>
                                </div>
                            </Button>
                        </PopoverTrigger>
                    </div>

                    <PopoverContent className="w-auto p-0 max-w-fit" align="start">
                        <div className="p-3">
                            <div className="text-sm font-semibold mb-3 text-gray-900">
                                {dateTarget === "from" ? "Select start date" : "Select end date"}
                            </div>

                            <Calendar
                                mode="single"
                                selected={dateTarget === "from" ? from : to}
                                onSelect={handleDateSelect}
                                numberOfMonths={2}
                                defaultMonth={(dateTarget === "from" ? from : to) ?? fyRange?.start}
                                disabled={(d) => {
                                    if (fyRange && (d < fyRange.start || d > fyRange.end)) {
                                        return true;
                                    }
                                    if (dateTarget === "to" && from) {
                                        return d < from;
                                    }
                                    return false;
                                }}
                                className="text-sm"
                                classNames={{
                                    months: "flex flex-row gap-4",
                                    month: "space-y-2",
                                    caption: "flex justify-center pt-1 relative items-center text-sm",
                                    table: "w-full border-collapse",
                                    head_row: "flex",
                                    head_cell: "text-muted-foreground rounded-md w-7 text-[11px]",
                                    row: "flex w-full mt-1",
                                    cell: "h-7 w-7 text-center text-xs p-0 relative",
                                    day: "h-7 w-7 p-0 text-xs",
                                }}
                            />

                            <div className="flex justify-between mt-4 pt-3 border-t">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => {
                                        onFromChange(undefined);
                                        onToChange(undefined);
                                    }}
                                >
                                    Clear
                                </Button>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setDateOpen(false)}
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
};

const Dropdowns: React.FC<DropdownsProps> = ({
    year,
    years,
    levels,
    loading,
    onSelectChange,
    onYearChange,
    onBatchSelect,
    dateFrom,
    onDateFromChange,
    dateTo,
    onDateToChange,
    lhcDropdownLevels = [],
    onLhcLevelChange,
}) => {
    const visibleLevelsDesc = React.useMemo(
        () => (levels || []).filter(l => l.visible).sort((a, b) => Number(b.id) - Number(a.id)),
        [levels]
    );

    return (
        <>
            {/* ROW 1: P&L Dropdowns */}
            <div className="grid gap-4 items-start w-full" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                {/* Year Dropdown */}
                <div className="min-w-0">
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Year</label>
                        <Combobox
                            options={(years && years.length > 0 ? years : [year]).map(y => ({ value: y, label: y }))}
                            value={year}
                            disabled={loading}
                            onValueChange={val => val && onYearChange?.(val)}
                            placeholder="Select year..."
                            buttonClassName="h-10 px-2.5 text-sm"
                        />
                    </div>
                </div>

                {/* Dynamic P&L Levels */}
                {visibleLevelsDesc.map(level => {
                    const baseOpts = Array.isArray(level.options)
                        ? level.options.map(opt => ({ value: opt.ou_code, label: opt.ou_name }))
                        : [];

                    const lockedCodes = Array.isArray(level.locked) ? level.locked : [];
                    const isLockedLevel = lockedCodes.length > 0;
                    const hasMultipleLocked = lockedCodes.length > 1;

                    const opts =
                        Number(level.id) !== 1 && isLockedLevel && hasMultipleLocked
                            ? [
                                ...baseOpts,
                                {
                                    value: LOCKED_MULTI_VALUE,
                                    label: `${lockedCodes.length} selected`,
                                },
                            ]
                            : baseOpts;

                    return (
                        <div className="min-w-0" key={String(level.id)}>
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">{level.name}</label>

                                {Number(level.id) === 1 ? (
                                    <MultiSelectCombobox
                                        options={baseOpts}
                                        value={
                                            isLockedLevel
                                                ? lockedCodes
                                                : Array.isArray(level.selected)
                                                    ? level.selected
                                                    : []
                                        }
                                        onValueChange={val => {
                                            if (isLockedLevel) return;
                                            onSelectChange(level.id, val);
                                        }}
                                        placeholder="-- select --"
                                        searchPlaceholder={`Search ${level.name.toLowerCase()}...`}
                                        emptyText={`No ${level.name.toLowerCase()} found.`}
                                        disabled={loading || isLockedLevel}
                                        buttonClassName="h-10 px-2.5 text-sm"
                                    />
                                ) : (
                                    <Combobox
                                        options={opts}
                                        value={
                                            isLockedLevel
                                                ? hasMultipleLocked
                                                    ? LOCKED_MULTI_VALUE
                                                    : lockedCodes[0] ?? ''
                                                : Array.isArray(level.selected) && level.selected.length > 0
                                                    ? level.selected[0]
                                                    : ''
                                        }
                                        onValueChange={val => {
                                            if (isLockedLevel) return;
                                            onSelectChange(level.id, val ? [val] : []);
                                        }}
                                        placeholder="-- select --"
                                        searchPlaceholder={`Search ${level.name.toLowerCase()}...`}
                                        emptyText={`No ${level.name.toLowerCase()} found.`}
                                        disabled={loading || isLockedLevel}
                                        buttonClassName="h-10 px-2.5 text-sm"
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>


            {/* ROW 2: Dates, LHC/Vendor + Dynamic LHC */}
            <div
                className="grid gap-4 items-end w-full"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}
            >
                <DateRangeFilter
                    from={dateFrom}
                    to={dateTo}
                    onFromChange={onDateFromChange}
                    onToChange={onDateToChange}
                    year={year}
                />

                {lhcDropdownLevels.map(level => (
                    <SingleSelectFilter
                        key={level.id}
                        label={level.name}
                        options={level.options.map(opt => opt.ou_code)}
                        value={level.selected?.[0] || ''}
                        onValueChange={val => onLhcLevelChange(level.id, val)}
                        disabled={loading}
                    />
                ))}
            </div>
        </>
    );
};

export default Dropdowns;
